import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatProfilePictureUrl } from '../utils/profilePictureHelper';
import { extractErrorMessage } from '../utils/errorUtils';
import { showError } from '../utils/showError';
import {
  PageHeader,
  SearchBar,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  Pagination,
  FormSelect,
  Card,
  CardHeader,
  CardBody
} from '../components/common';

const BuildingJoinRequests = () => {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [building, setBuilding] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchBuilding();
    fetchUnits();
    fetchJoinRequests();
  }, [buildingId, currentPage, searchTerm]);

  const fetchBuilding = async () => {
    try {
      const response = await api.get(`/Building/${buildingId}`);
      setBuilding(response.data);
    } catch (err) {
      console.error('Error fetching building:', err);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await api.get(`/Building/${buildingId}/units`);
      setUnits(response.data.items || []);
    } catch (err) {
      console.error('Error fetching units:', err);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        ...(searchTerm && { searchString: searchTerm })
      };

      const response = await api.get(`/Building/${buildingId}/join-requests`, { params });
      setRequests(response.data.items || []);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      setError(extractErrorMessage(err));
      console.error('Error fetching join requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchJoinRequests();
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApproveRequest = async (userId) => {
    if (!selectedUnit) {
      alert('Please select a building unit before approving the request.');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/Building/${buildingId}/join-requests/${userId}/approve`, null, {
        params: { buildingUnitId: selectedUnit }
      });
      fetchJoinRequests();
      setSelectedUnit('');
      setSuccess('Join request approved successfully!');
    } catch (err) {
      console.error('Error approving request:', err);
      showError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async (userId) => {
    if (window.confirm('Are you sure you want to reject this join request?')) {
      setActionLoading(true);
      try {
        await api.post(`/api/admin/Building/${buildingId}/join-requests/${userId}/reject`);
        fetchJoinRequests();
        setSuccess('Join request rejected successfully!');
      } catch (err) {
        console.error('Error rejecting request:', err);
        showError(err);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const getRoleBadges = (request) => {
    const badges = [];
    if (request.isAdmin) badges.push({ label: 'Admin', color: 'bg-red-100 text-red-800' });
    if (request.isManager) badges.push({ label: 'Manager', color: 'bg-purple-100 text-purple-800' });
    if (request.isOwner) badges.push({ label: 'Owner', color: 'bg-green-100 text-green-800' });
    if (request.isResident) badges.push({ label: 'Resident', color: 'bg-blue-100 text-blue-800' });
    if (request.isStaff) badges.push({ label: 'Staff', color: 'bg-yellow-100 text-yellow-800' });
    return badges;
  };

  if (loading && requests.length === 0) {
    return <LoadingSpinner size="large" />;
  }

  const [success, setSuccess] = useState(null);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Join Requests - ${building?.name || 'Loading...'}`}
        backLink={{ label: "Back to Building Details", href: `/buildings/${buildingId}` }}
        actions={[
          <Button
            key="members"
            onClick={() => navigate(`/buildings/${buildingId}/members`)}
            variant="accent"
          >
            View Members
          </Button>,
          <Button
            key="units"
            onClick={() => navigate(`/buildings/${buildingId}/units`)}
            variant="secondary"
          >
            View Units
          </Button>
        ]}
      />

      <Card>
        <CardBody>
          <SearchBar
            placeholder="Search join requests..."
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
          />
        </CardBody>
      </Card>

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      {success && (
        <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />
      )}

      {requests.length > 0 && (
        <Card>
          <CardHeader
            title="Unit Assignment"
            subtitle="Select a building unit to assign when approving join requests"
          />
          <CardBody>
            <FormSelect
              label="Select Unit"
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              options={[
                { value: '', label: 'Select a unit...' },
                ...units.map((unit) => ({
                  value: unit.buildingUnitId,
                  label: `Unit ${unit.number || unit.buildingUnitId}${unit.floor ? ` - Floor ${unit.floor}` : ''}${(!unit.ownerId && !unit.residentId && !unit.staffId) ? ' (Available)' : ''}`
                }))
              ]}
            />
          </CardBody>
        </Card>
      )}

      <ListContainer>
        {requests.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No join requests found"
            description="There are currently no pending join requests for this building."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell header>User</TableCell>
                <TableCell header>Contact</TableCell>
                <TableCell header>Roles Requested</TableCell>
                <TableCell header>Details</TableCell>
                <TableCell header>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.userId}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <img
                        className="h-10 w-10 rounded-full object-cover border border-slate-200"
                        src={request.profilePictureUrl ? formatProfilePictureUrl(request.profilePictureUrl) : `https://ui-avatars.com/api/?name=${encodeURIComponent(request.firstName + ' ' + request.lastName)}&background=random`}
                        alt=""
                      />
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                        {request.firstName} {request.lastName}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-700">
                      <div>{request.email || 'N/A'}</div>
                      {request.mobilePhone && <div className="mt-0.5">{request.mobilePhone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {getRoleBadges(request).map((badge, index) => (
                        <Badge
                          key={index}
                          variant={badge.color.includes('red') ? 'danger' : badge.color.includes('purple') ? 'secondary' : badge.color.includes('green') ? 'success' : badge.color.includes('blue') ? 'info' : 'warning'}
                          size="small"
                        >
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-500 max-w-xs break-words">
                      {request.description || <span className="text-slate-400 italic">No description provided</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end space-x-2 pr-4">
                      <Button
                        onClick={() => handleApproveRequest(request.userId)}
                        disabled={actionLoading || !selectedUnit}
                        variant="accent"
                        size="small"
                        loading={actionLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectRequest(request.userId)}
                        disabled={actionLoading}
                        variant="danger"
                        size="small"
                        loading={actionLoading}
                      >
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default BuildingJoinRequests;